/**
 * Canonical telemetry envelope schema (per spec §1.5.9).
 * Validated at the worker boundary; untrusted JSON never reaches the main thread.
 */
import { z } from 'zod';

export const MetaEnvelope = z.object({
  apType: z.enum(['px4', 'ardupilot', 'dji', 'custom']),
  apFirmware: z.string().optional(),
  rawFlightMode: z.union([z.number(), z.string()]).optional(),
  nullFields: z.array(z.string()).optional(),
  msgSources: z.record(z.string()).optional(),
  conversionFlags: z.record(z.string()).optional(),
  clockOffset: z.number().optional(),
});

export const Pred1Min = z.object({
  lat: z.number(),
  lng: z.number(),
  inside: z.boolean().nullable().optional(),
});

/** Remote-ID (ASTM F3411) broadcast fields, present only on `origin: 'netid'`
 *  drones. Surfaced by the External drone detail tab (cross-tenant feature).
 *  Position / velocity live on the parent envelope; this carries the
 *  identity + integrity fields unique to a Network-ID broadcast. */
export const RemoteIdEnvelope = z.object({
  uas_id: z.string(),
  operator_id: z.string().nullable(),
  ua_type: z.string().nullable(),
  emergency_status: z.string().nullable(),
  horiz_accuracy_m: z.number().nullable(),
  vert_accuracy_m: z.number().nullable(),
  last_broadcast_at: z.string().nullable(),
});
export type RemoteIdEnvelope = z.infer<typeof RemoteIdEnvelope>;

export const TelemetryEnvelope = z.object({
  drone_id: z.string(),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  heading: z.number().min(0).max(360),
  /** False when the source sent no heading (or F3411's 361 = "unknown") and `heading`
   *  is only the 0° default the marker needs. Absent ⇒ treat as known. */
  heading_known: z.boolean().optional(),
  asl: z.number().nullable(), // metres
  agl: z.number().nullable(), // feet (D1 unresolved — ft primary per mockup)
  aglM: z.number().nullable(),
  atl: z.number().nullable(),
  atlM: z.number().nullable(),
  battery_pct: z.number().min(0).max(100).nullable(),
  battery_v: z.number().nullable(),
  flight_mode: z.string().nullable(),
  flight_time: z.string().nullable(), // "HH:MM"
  gps_sats: z.number().int().nullable(),
  gps_hdop: z.number().nullable(),
  gps_lock: z.enum(['Acquired', 'Acquiring', 'Lost']).nullable(),
  groundspeed: z.number().nullable(),
  climbRate: z.number().nullable(),
  /** Longitudinal acceleration (m/s²). Used by Flight Ops Position
   *  Prediction accordion; not currently published by v1's networkid. */
  acceleration: z.number().nullable(),
  signal: z.number().min(0).max(100).nullable(),
  ops_status: z.enum(['Normal', 'Degraded', 'Non-Normal']).nullable(),
  status: z.enum(['Active', 'DAA', 'DAA-DEG', 'SA', 'LOST']).nullable(),
  status_label: z.string().nullable(),
  confidence: z.number().min(0).max(100).nullable(),
  proximity: z.enum(['safe', 'caution', 'alert']).nullable(),
  conflict: z.string().nullable(),
  pred1min: Pred1Min.nullable(),
  // Static-from-config fields included in envelope for convenience
  callsign: z.string(),
  drone_model: z.string(),
  drone_type: z.string(), // VTOL / Multirotor / Fixed-wing
  nmacRadius: z.number().default(304.8), // m
  swcRadius: z.number().default(1219.2), // m
  evalRadius: z.number().default(12000), // m
  // Cross-tenant partnership provenance (006/007 cross-tenant feature).
  // Derived from the arrival topic + the connector-injected `partner_tenant`
  // field: own-tenant topic → 'internal'; `flysafe/v1/partner/{T}/telemetry`
  // → 'partner'; `networkid/#` (ASTM F3411) → 'netid'. Absent ⇒ 'internal'.
  origin: z.enum(['internal', 'partner', 'netid']).optional(),
  partner_tenant: z.string().nullable().optional(),
  /** Telemetry source label for the fleet card badge (MAVLink / DJI Flight
   *  Hub / ADS-B Out / Partner Stream / Network ID / No Signal). */
  source_label: z.string().nullable().optional(),
  /** Remote-ID broadcast detail, present only on `origin: 'netid'`. */
  remote_id: RemoteIdEnvelope.optional(),

  /* ---- `v1/output` namespace additions (ticket 86e2jbe3d) -------------
   * Fields the new NID topics publish that had no home on the envelope.
   * All optional + nullable so the legacy path is unaffected, and all
   * retained even where no UI reads them yet — an unmapped field is a
   * decision to be made, not data to be dropped. */

  /** ASTM F3411 `operationalStatus`, verbatim ("Airborne", "Ground",
   *  "Emergency", "Undeclared"). Distinct from `ops_status`, which is our
   *  own Normal/Degraded/Non-Normal health scale — this is flight phase. */
  flight_phase: z.string().nullable().optional(),
  /** Qualifies which altitude `height` represents. "AGL" → the value
   *  belongs in agl/aglM; "TAKEOFF_RELATIVE" → atl/atlM. Before this
   *  field existed the adapter mirrored AGL into ATL. */
  height_type: z.string().nullable().optional(),
  /** F3411 integrity fields (metres / m·s⁻¹ / seconds). */
  vert_accuracy_m: z.number().nullable().optional(),
  speed_accuracy_mps: z.number().nullable().optional(),
  timestamp_accuracy_s: z.number().nullable().optional(),
  /** Free-text operator description broadcast with Remote ID. */
  self_id_text: z.string().nullable().optional(),
  /** Provenance tag — "Simulated" for the sandbox fleet. Lets the UI
   *  distinguish a simulator from a real aircraft. */
  track_origin: z.string().nullable().optional(),
  /** Publisher that produced the message ("aircraft_agent"). */
  source_name: z.string().nullable().optional(),
  /** Tenant that owns the aircraft, from the payload rather than the
   *  topic — the `v1/output` path no longer carries a tenant segment. */
  tenant_id: z.string().nullable().optional(),

  /** Airframe state from the telemetry topic. */
  armed: z.boolean().nullable().optional(),
  /** MAV_LANDED_STATE label ("On ground" / "In air" / "Takeoff" / "Landing"). MAVLink only. */
  landed_state: z.string().nullable().optional(),
  adapter_connected: z.boolean().nullable().optional(),
  pressure_altitude: z.number().nullable().optional(),
  /** Link RSSI in dBm. MAVLink only, and only when the plugin adds RADIO_STATUS.link_rssi_dbm. */
  link_rssi_dbm: z.number().nullable().optional(),

  /** From the raw GCS stream (`v1/input/aircraft/{sn}/gcs_telemetry`), which is only
   *  subscribed for the selected aircraft. MAVLink VFR_HUD / ATTITUDE / SCALED_PRESSURE. */
  airspeed_mps: z.number().nullable().optional(),
  throttle_pct: z.number().nullable().optional(),
  roll_deg: z.number().nullable().optional(),
  pitch_deg: z.number().nullable().optional(),
  yaw_deg: z.number().nullable().optional(),
  air_pressure_hpa: z.number().nullable().optional(),
  air_temp_c: z.number().nullable().optional(),
  /** MAVLink TERRAIN_REPORT: terrain elevation under the aircraft (m AMSL) and the
   *  aircraft's height above it (m). Feeds the Terrain tab's Ground Clearance. */
  terrain_elev_m: z.number().nullable().optional(),
  terrain_clearance_m: z.number().nullable().optional(),

  /** Pipeline health. `pipeline_delay_s` is the backend's own measure of
   *  end-to-end lag, which beats guessing from our clock; `sequence` is
   *  monotonic per drone so gaps mean dropped messages; `trace_id` links
   *  a stalled drone to its Grafana/Tempo trace. */
  pipeline_delay_s: z.number().nullable().optional(),
  sequence: z.number().nullable().optional(),
  trace_id: z.string().nullable().optional(),
  schema_version: z.string().nullable().optional(),
  // Provenance
  _meta: MetaEnvelope.optional(),
  // Accept both UTC "Z" and explicit offset forms ("+00:00", "-05:00").
  // v1's networkid publishes a mix of both; rejecting offset-style strings
  // silently dropped 2/3 drones during the first live test.
  received_at: z.string().datetime({ offset: true }),
});

export type TelemetryEnvelope = z.infer<typeof TelemetryEnvelope>;
